import React, { useState } from 'react';
import { Search, UserPlus, X, Users } from 'lucide-react';
import { Card } from '../BossUI';
import { StudentDetailsModal } from './StudentDetailsModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Assignment Modal Component
const AssignmentModal = ({ student, onClose, onAssign }) => {
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  // Fetch therapists
  const { data: therapistsData, isLoading: loadingTherapists } = useQuery({
    queryKey: ['therapists-for-assignment'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/therapists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch therapists');
      return response.json();
    },
  });

  // Fetch current assignments
  const { data: assignmentsData, isLoading: loadingAssignments } = useQuery({
    queryKey: ['student-assignments', student.id],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${student.id}/assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch assignments');
      return response.json();
    },
  });

  const handleAssign = async () => {
    if (!selectedTherapist) return;
    
    setIsLoading(true);
    try {
      await onAssign(student.id, selectedTherapist);
      onClose();
    } catch (error) {
      console.error('Assignment failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassign = async (therapistId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${student.id}/unassign/${therapistId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to unassign therapist');
      }

      // Refresh assignments
      window.location.reload();
    } catch (error) {
      console.error('Unassignment failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (therapistId, currentIsActive) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${student.id}/assignment/${therapistId}/status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !currentIsActive }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.msg || 'Failed to update status');
      }

      queryClient.invalidateQueries(['student-assignments', student.id]);
    } catch (error) {
      alert(`Update failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingTherapists || loadingAssignments) {
    return (
      <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
          <div className="flex justify-center">Loading...</div>
        </div>
      </div>
    );
  }

  // Get assigned therapist IDs to filter them out from the dropdown
  const assignedTherapistIds = assignmentsData?.assignments?.map(a => a.therapistId) || [];
  const availableTherapists = therapistsData?.therapists?.filter(
    t => !assignedTherapistIds.includes(t.id)
  ) || [];

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            Manage Assignments - {student.firstName} {student.lastName}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Assignments */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Current Assignments</h4>
            {assignmentsData?.assignments?.length > 0 ? (
              <div className="space-y-2">
                {assignmentsData.assignments.map((assignment) => (
                  <div key={assignment.therapistId} className="flex items-center justify-between p-2 bg-slate-50 rounded-md">
                    <span className="text-sm text-slate-900">
                      {assignment.therapistName}
                      <span className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${assignment.isActive === false ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'}`}>
                        {assignment.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleToggleActive(assignment.therapistId, assignment.isActive !== false)}
                        disabled={isLoading}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50"
                      >
                        {assignment.isActive === false ? 'Reactivate' : 'Mark Inactive'}
                      </button>
                      <button
                        onClick={() => handleUnassign(assignment.therapistId)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No current assignments</p>
            )}
          </div>

          {/* Add New Assignment */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-3">Add New Assignment</h4>
            <div className="space-y-3">
              <select
                value={selectedTherapist}
                onChange={(e) => setSelectedTherapist(e.target.value)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2"
                disabled={isLoading || availableTherapists.length === 0}
              >
                <option value="">
                  {availableTherapists.length === 0 ? 'All therapists assigned' : 'Select a therapist'}
                </option>
                {availableTherapists.map((therapist) => (
                  <option key={therapist.id} value={therapist.id}>
                    {therapist.firstName} {therapist.lastName}
                  </option>
                ))}
              </select>
              
              <button
                onClick={handleAssign}
                disabled={!selectedTherapist || isLoading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Assigning...' : 'Assign Therapist'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom hooks for data fetching
const useStudentData = () => {
  const token = localStorage.getItem('wmf_token');
  
  const fetchStudentDetails = async (studentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch student details');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching student details:', error);
      throw error;
    }
  };

  return { fetchStudentDetails };
};

export const MyStudentsView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const { fetchStudentDetails } = useStudentData();
  const navigate = useNavigate();

  const handleCreateReport = (student) => {
    navigate('/dashboard/reports/create', {
      state: { studentId: student.id, studentName: `${student.firstName} ${student.lastName}` }
    });
  };

  const fetchStudents = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/therapists/my-students${showInactive ? '?includeInactive=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      const data = await response.json();
      setStudents(data.students || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [showInactive]);

  // Refetch whenever the inactive toggle changes
  React.useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleToggleActive = async (student) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/students/${student.id}/assignment/${student.therapistId}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive: !student.isActive }),
        }
      );
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.msg || 'Failed to update status');
      }
      await fetchStudents();
    } catch (err) {
      alert(`Update failed: ${err.message}`);
    }
  };

  const handleViewDetails = async (studentId) => {
    try {
      const details = await fetchStudentDetails(studentId);
      setSelectedStudent(details);
    } catch (error) {
      console.error('Error fetching student details:', error);
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  const filteredStudents = students.filter(student => 
    `${student.firstName} ${student.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-900">My Students</h2>
        <p className="mt-1 text-sm text-slate-500">View your assigned students</p>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="max-w-md flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -mt-2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search students..."
              className="boss-input pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive students
        </label>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Military Branch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Military Status
                </th>
                {showInactive && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Assignment
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredStudents.map((student) => (
                <tr key={student.id} className={student.isActive === false ? 'bg-slate-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {student.firstName} {student.lastName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {student.militaryBranch}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {student.militaryStatus}
                  </td>
                  {showInactive && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${student.isActive === false ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'}`}>
                        {student.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                    {student.isActive === false ? (
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleToggleActive(student)}
                      >
                        Reactivate
                      </button>
                    ) : (
                      <>
                        <button
                          className="text-green-600 hover:text-green-900"
                          onClick={() => handleCreateReport(student)}
                        >
                          Create Session Report
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => handleViewDetails(student.id)}
                        >
                          View Details
                        </button>
                        <button
                          className="text-slate-500 hover:text-slate-700"
                          onClick={() => handleToggleActive(student)}
                        >
                          Mark Inactive
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedStudent && (
        <StudentDetailsModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
};

export const AllStudentsView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [assignmentModalStudent, setAssignmentModalStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchStudentDetails } = useStudentData();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is admin or manager
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  // Mutation for assigning students
  const assignStudentMutation = useMutation({
    mutationFn: async ({ studentId, therapistId }) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${studentId}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ therapistId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to assign student');
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Show success message
      alert(`Student assigned successfully to ${data.assignedTo}`);
      // Refresh the assignments data
      queryClient.invalidateQueries(['student-assignments']);
    },
    onError: (error) => {
      alert(`Assignment failed: ${error.message}`);
    }
  });

  // Fetch students on component mount
  React.useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/students/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch students');
        }

        const data = await response.json();
        setStudents(data.students || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const handleAssignStudent = async (studentId) => {
    if (isAdminOrManager) {
      // Open assignment modal for admin/manager
      const student = students.find(s => s.id === studentId);
      setAssignmentModalStudent(student);
    } else {
      // Direct assignment for regular therapists (existing behavior)
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/students/${studentId}/assign`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.msg || 'Failed to assign student');
        }

        const data = await response.json();
        alert(`Student assigned successfully to ${data.assignedTo}`);
        window.location.reload();
      } catch (error) {
        alert(`Assignment failed: ${error.message}`);
      }
    }
  };

  const handleAssignmentModalAssign = async (studentId, therapistId) => {
    await assignStudentMutation.mutateAsync({ studentId, therapistId });
  };

  const handleViewDetails = async (studentId) => {
    try {
      const details = await fetchStudentDetails(studentId);
      setSelectedStudent(details);
    } catch (error) {
      console.error('Error fetching student details:', error);
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  const filteredStudents = students.filter(student => 
    `${student.firstName} ${student.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-900">All Students</h2>
        <p className="mt-1 text-sm text-slate-500">
          {isAdminOrManager ? 'View and assign students to therapists' : 'View and assign students to yourself'}
        </p>
      </div>

      <div className="max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -mt-2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search students..."
            className="boss-input pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Military Branch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {student.firstName} {student.lastName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {student.militaryBranch}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {student.militaryStatus}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                    <button 
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => handleAssignStudent(student.id)}
                    >
                      <UserPlus className="h-4 w-4 inline mr-1" />
                      {isAdminOrManager ? 'Manage Assignments' : 'Assign to Me'}
                    </button>
                    <button 
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => handleViewDetails(student.id)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedStudent && (
        <StudentDetailsModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}

      {assignmentModalStudent && (
        <AssignmentModal
          student={assignmentModalStudent}
          onClose={() => setAssignmentModalStudent(null)}
          onAssign={handleAssignmentModalAssign}
        />
      )}
    </div>
  );
};

// Structured fields a therapist fills in to complete a student record,
// shared by the HubSpot-sourced flow below. Mirrors the fields the legacy
// free-text parser extracts in LegacyAddStudentView.
const STUDENT_FORM_FIELDS = [
  { key: 'age', label: 'Age', type: 'number' },
  { key: 'pocPhone', label: 'POC Phone' },
  { key: 'pocAddress', label: 'POC Address' },
  { key: 'militaryAffiliation', label: "Student's Military Affiliation" },
  { key: 'militaryBranch', label: 'Military Branch' },
  { key: 'militaryStatus', label: 'Military Status' },
  { key: 'yearsServed', label: 'Years Served' },
  { key: 'goals', label: 'Student Goals' },
  { key: 'desiredInstruments', label: 'Desired Instruments' },
  { key: 'currentExperience', label: 'Current Experience' },
  { key: 'previousExperience', label: 'Previous Experience' },
  { key: 'individualLessonInterest', label: 'Interest in Individual Lessons' },
  { key: 'onlineLessonInterest', label: 'Interest in Online/Band Sessions' },
  { key: 'ptsd_history', label: 'PTSD History' },
  { key: 'tbiHistory', label: 'TBI History' },
  { key: 'anxietyDepressionHistory', label: 'Anxiety/Depression History' },
  { key: 'howDiscoverWMF', label: 'How Did You Discover WMF?' },
  { key: 'campaigns', label: 'Campaigns' },
];

// HubSpot Contact lookup tab: search, pick a contact, complete the
// remaining therapy-specific fields, then submit.
const HubSpotAddView = () => {
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [form, setForm] = useState({});
  const [quickAddingId, setQuickAddingId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(query.trim()), 400);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: searchData, isFetching: searching, error: searchError } = useQuery({
    queryKey: ['hubspot-contacts', searchTerm],
    queryFn: async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/students/hubspot/search?q=${encodeURIComponent(searchTerm)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.msg || 'HubSpot search failed');
      }
      return response.json();
    },
    enabled: searchTerm.length > 1,
  });

  const contacts = searchData?.contacts || [];

  const addMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/add-hubspot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.msg || 'Failed to add student');
      }
      return data;
    },
    onSuccess: () => {
      setSuccess(true);
      setQuickAddingId(null);
      setTimeout(() => navigate('/dashboard/'), 2000);
    },
    onError: (err) => {
      setError(err.message);
      setQuickAddingId(null);
    },
  });

  // "Add Student": use the HubSpot data as-is, no review step. This covers
  // the common case where the foundation's HubSpot contact is already the
  // full record (no additional student data beyond what HubSpot provides).
  const handleQuickAdd = (contact) => {
    setError(null);
    setSuccess(false);
    setQuickAddingId(contact.hubspotContactId);
    addMutation.mutate(contact);
  };

  // "Modify and Add": review/complete fields before submitting, for the
  // less-common case where the record needs changes beyond HubSpot's data.
  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setForm({ ...contact });
    setError(null);
    setSuccess(false);
  };

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    addMutation.mutate(form);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 text-green-800 border-green-200">
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>Student added successfully! Redirecting...</AlertDescription>
        </Alert>
      )}

      {!selectedContact && (
        <div className="space-y-3">
          <label htmlFor="hubspotSearch" className="block text-sm font-medium text-gray-700">
            Search HubSpot Contacts (name or email)
          </label>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -mt-2 h-4 w-4 text-slate-400" />
            <input
              id="hubspotSearch"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a name or email..."
              className="boss-input pl-10"
            />
          </div>

          {searching && <p className="text-sm text-slate-500">Searching...</p>}
          {searchError && (
            <p className="text-sm text-red-600">{searchError.message}</p>
          )}

          {!searching && searchTerm.length > 1 && contacts.length === 0 && !searchError && (
            <p className="text-sm text-slate-500">No HubSpot contacts found.</p>
          )}

          {contacts.length > 0 && (
            <Card className="overflow-hidden">
              <ul className="divide-y divide-slate-200">
                {contacts.map((contact) => (
                  <li
                    key={contact.hubspotContactId}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {contact.firstname} {contact.lastname}
                      </p>
                      <p className="text-sm text-slate-500">{contact.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSelectContact(contact)}
                        disabled={addMutation.isPending}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
                      >
                        Modify and Add
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuickAdd(contact)}
                        disabled={addMutation.isPending}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {quickAddingId === contact.hubspotContactId && addMutation.isPending
                          ? 'Adding...'
                          : 'Add Student'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {selectedContact && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-slate-700">
                Adding {form.firstname} {form.lastname} ({form.email})
              </h3>
              <p className="text-sm text-slate-500">
                Complete the remaining fields below, then add the student.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setSelectedContact(null); setForm({}); }}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Choose a different contact
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {STUDENT_FORM_FIELDS.map(({ key, label, type }) => (
              <div key={key}>
                <label htmlFor={key} className="block text-sm font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <input
                  id={key}
                  type={type || 'text'}
                  value={form[key] ?? ''}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  className="boss-input"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/students')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addMutation.isPending}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 ${
                addMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {addMutation.isPending ? 'Adding...' : 'Add Student'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const LegacyAddStudentView = () => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/students/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.msg || 'Failed to add student');
      }

      const data = await response.json();
      setSuccess(true);
      setText('');
      
      // Show success message for 2 seconds before redirecting
      setTimeout(() => {
        navigate('/dashboard/');
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6 bg-green-50 text-green-800 border-green-200">
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>Student added successfully! Redirecting...</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="studentText"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Paste Student Information
          </label>
          <textarea
            id="studentText"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Paste the formatted student information here..."
            required
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/students')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !text.trim()}
            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              loading || !text.trim() ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Adding...</span>
              </div>
            ) : (
              'Add Student'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AddStudentView = () => {
  const [tab, setTab] = useState('hubspot');

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold">Add New Student</h1>

      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {[
            { id: 'hubspot', label: 'HubSpot Lookup' },
            { id: 'legacy', label: 'Legacy Entry' },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'hubspot' && <HubSpotAddView />}
      {tab === 'legacy' && <LegacyAddStudentView />}
    </div>
  );
};
